import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, ChevronRight, Star, FolderOpen, BookOpen, FileText, ClipboardList,
  Monitor, Trash2, Copy, Archive, ArchiveRestore, Pencil, Check,
  SlidersHorizontal, ChevronDown,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useResources, type SavedResource } from './resources-context';

// ── Type metadata ─────────────────────────────────────────────────────────────
const TYPE_META = {
  lesson:       { label: 'Lesson',       color: 'bg-blue-100 text-blue-700',        icon: BookOpen      },
  activity:     { label: 'Activity',     color: 'bg-violet-100 text-violet-700',    icon: FileText      },
  assessment:   { label: 'Assessment',   color: 'bg-emerald-100 text-emerald-700',  icon: ClipboardList },
  presentation: { label: 'Presentation', color: 'bg-amber-100 text-amber-700',      icon: Monitor       },
} as const;

const GRADE_ORDER: Record<string, number> = {
  K: 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8,
};

type ResourceType = keyof typeof TYPE_META;
type TypeFilter   = 'all' | ResourceType;
type SortKey      = 'viewed' | 'edited' | 'created' | 'title' | 'grade' | 'subject';

const SORT_LABELS: Record<SortKey, string> = {
  viewed:  'Recently viewed',
  edited:  'Recently edited',
  created: 'Date created',
  title:   'Title (A–Z)',
  grade:   'Grade',
  subject: 'Subject',
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  onBack:         () => void;
  onOpenResource: (resource: SavedResource) => void;
}

export function MyResourcesPage({ onBack, onOpenResource }: Props) {
  const { resources, deleteResource, duplicateResource, updateResource, toggleFavorite } = useResources();

  const [search,       setSearch]       = useState('');
  const [typeFilter,   setTypeFilter]   = useState<TypeFilter>('all');
  const [sortBy,       setSortBy]       = useState<SortKey>('viewed');
  const [showFavOnly,  setShowFavOnly]  = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sortOpen,     setSortOpen]     = useState(false);
  const [deleteId,     setDeleteId]     = useState<string | null>(null);
  const [renameId,     setRenameId]     = useState<string | null>(null);
  const [renameVal,    setRenameVal]    = useState('');

  const renameRef = useRef<HTMLInputElement>(null);

  // ── Filtering + sorting ───────────────────────────────────────────────────
  const filtered = resources
    .filter(r => (showArchived ? r.isArchived : !r.isArchived))
    .filter(r => typeFilter === 'all' || r.type === typeFilter)
    .filter(r => !showFavOnly || r.isFavorite)
    .filter(r => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      return [r.title, r.grade, r.subject, r.topic ?? '', r.standard ?? '']
        .some(v => v.toLowerCase().includes(q));
    });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'viewed':  return ts(b.lastViewedAt ?? b.savedAt) - ts(a.lastViewedAt ?? a.savedAt);
      case 'edited':  return ts(b.lastEditedAt) - ts(a.lastEditedAt);
      case 'created': return ts(b.savedAt) - ts(a.savedAt);
      case 'title':   return a.title.localeCompare(b.title);
      case 'grade':   return (GRADE_ORDER[a.grade] ?? 9) - (GRADE_ORDER[b.grade] ?? 9);
      case 'subject': return a.subject.localeCompare(b.subject);
      default: return 0;
    }
  });

  // ── Stats (active only) ───────────────────────────────────────────────────
  const active = resources.filter(r => !r.isArchived);
  const counts: Record<ResourceType, number> = {
    lesson:       active.filter(r => r.type === 'lesson').length,
    activity:     active.filter(r => r.type === 'activity').length,
    assessment:   active.filter(r => r.type === 'assessment').length,
    presentation: active.filter(r => r.type === 'presentation').length,
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  function startRename(r: SavedResource) {
    setRenameId(r.id);
    setRenameVal(r.title);
    setTimeout(() => { renameRef.current?.focus(); renameRef.current?.select(); }, 40);
  }

  function commitRename() {
    if (renameId && renameVal.trim()) updateResource(renameId, { title: renameVal.trim() });
    setRenameId(null);
  }

  const hasFilters = !!search || typeFilter !== 'all' || showFavOnly;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-12">

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setDeleteId(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full z-10">
              <p className="text-slate-800 font-semibold text-center mb-1">Delete Resource?</p>
              <p className="text-slate-500 text-sm text-center mb-6">
                This resource will be permanently removed. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteId(null)}
                  className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button onClick={() => { deleteResource(deleteId); setDeleteId(null); }}
                  className="flex-1 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors">
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
          <ChevronRight size={18} className="rotate-180" />
        </button>
        <div className="flex items-center gap-2.5 flex-1">
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <FolderOpen size={18} className="text-slate-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 leading-tight">My Resources</h2>
            <p className="text-xs text-slate-500">
              {active.length === 0
                ? 'No saved resources yet'
                : `${active.length} resource${active.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowArchived(v => !v); setTypeFilter('all'); }}
          className={`flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold transition-all border
            ${showArchived
              ? 'bg-slate-900 text-white border-slate-900'
              : 'text-slate-500 border-slate-200 hover:bg-slate-50 hover:text-slate-700'}`}>
          {showArchived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
          {showArchived ? 'Archived view' : 'Archive'}
        </button>
      </div>

      {/* ── Type filter tabs (active view only) ──────────────────────────── */}
      {active.length > 0 && !showArchived && (
        <div className="flex flex-wrap gap-2">
          {([
            ['all',          `All (${active.length})`,              'bg-slate-100 text-slate-700'],
            ['lesson',       `Lessons (${counts.lesson})`,          'bg-blue-50 text-blue-700'],
            ['activity',     `Activities (${counts.activity})`,        'bg-violet-50 text-violet-700'],
            ['presentation', `Presentations (${counts.presentation})`, 'bg-amber-50 text-amber-700'],
            ['assessment',   `Assessments (${counts.assessment})`,     'bg-emerald-50 text-emerald-700'],
          ] as [TypeFilter, string, string][])
            .filter(([k]) => k === 'all' || counts[k as ResourceType] > 0)
            .map(([k, label, color]) => (
              <button key={k} onClick={() => setTypeFilter(k)}
                className={`h-7 px-3 rounded-full text-xs font-medium transition-all
                  ${typeFilter === k
                    ? `${color} ring-2 ring-offset-1 ring-current`
                    : `${color} opacity-60 hover:opacity-90`}`}>
                {label}
              </button>
            ))}
        </div>
      )}

      {/* ── Search + Sort + Favorites row ────────────────────────────────── */}
      {resources.length > 0 && (
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title, grade, subject, topic, or standard…"
              className="pl-8 h-9 rounded-xl border-slate-200 text-sm"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={13} />
              </button>
            )}
          </div>

          {/* Favorites toggle */}
          <button onClick={() => setShowFavOnly(v => !v)} title="Show favorites only"
            className={`h-9 w-9 rounded-xl border flex items-center justify-center transition-all flex-shrink-0
              ${showFavOnly
                ? 'bg-amber-100 border-amber-300 text-amber-500'
                : 'border-slate-200 text-slate-400 hover:text-amber-400 hover:border-amber-200 hover:bg-amber-50'}`}>
            <Star size={15} fill={showFavOnly ? 'currentColor' : 'none'} />
          </button>

          {/* Sort dropdown */}
          <div className="relative flex-shrink-0">
            <button onClick={() => setSortOpen(v => !v)}
              className="h-9 px-3 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5 hover:bg-slate-50 transition-colors">
              <SlidersHorizontal size={13} />
              <span className="hidden sm:inline">{SORT_LABELS[sortBy]}</span>
              <ChevronDown size={11} className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {sortOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} />
                  <motion.div initial={{ opacity: 0, y: -4, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.97 }} transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-1.5 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20">
                    {(Object.entries(SORT_LABELS) as [SortKey, string][]).map(([k, label]) => (
                      <button key={k} onClick={() => { setSortBy(k); setSortOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors
                          ${sortBy === k
                            ? 'bg-slate-50 font-semibold text-slate-900'
                            : 'text-slate-600 hover:bg-slate-50'}`}>
                        {label}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* ── Resource list ─────────────────────────────────────────────────── */}
      {sorted.length === 0 ? (
        /* Empty states */
        resources.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-16 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center">
              <FolderOpen size={26} className="text-slate-300" />
            </div>
            <div>
              <p className="font-semibold text-slate-600 mb-1">Nothing saved yet</p>
              <p className="text-slate-400 text-sm max-w-xs leading-relaxed">
                Generate a lesson, activity sheet, presentation, or assessment and save it
                to start building your library.
              </p>
            </div>
            <button onClick={onBack}
              className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
              Create your first lesson →
            </button>
          </div>
        ) : showArchived ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center text-center gap-3">
            <Archive size={28} className="text-slate-200" />
            <p className="text-slate-500 font-medium text-sm">No archived resources</p>
            <p className="text-xs text-slate-400">
              Archive a resource to hide it from your main library without deleting it.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center text-center gap-3">
            <Search size={28} className="text-slate-200" />
            <p className="text-slate-500 font-medium text-sm">No matching resources</p>
            {hasFilters && (
              <button
                onClick={() => { setSearch(''); setTypeFilter('all'); setShowFavOnly(false); }}
                className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                Clear filters
              </button>
            )}
          </div>
        )
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {sorted.map((r, i) => {
            const meta      = TYPE_META[r.type as ResourceType] ?? TYPE_META.lesson;
            const MetaIcon  = meta.icon;
            const isRenaming = renameId === r.id;
            const gradeLabel = r.grade === 'K' ? 'Kindergarten' : `Grade ${r.grade}`;
            const viewedAgo  = r.lastViewedAt ? relativeDate(r.lastViewedAt) : null;
            const editedAgo  = relativeDate(r.lastEditedAt ?? r.savedAt);

            return (
              <div key={r.id}
                className={`flex items-start gap-3 px-4 py-4 group transition-colors hover:bg-slate-50/70
                  ${i < sorted.length - 1 ? 'border-b border-slate-100' : ''}`}>

                {/* Type icon */}
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5
                  ${meta.color.split(' ')[0]}`}>
                  <MetaIcon size={16} className={meta.color.split(' ')[1]} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {isRenaming ? (
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        ref={renameRef}
                        value={renameVal}
                        onChange={e => setRenameVal(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') setRenameId(null);
                        }}
                        onBlur={commitRename}
                        className="flex-1 text-sm font-semibold text-slate-900 bg-white border border-primary/40 rounded-lg px-2 py-0.5 outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      <button onClick={commitRename}
                        className="h-6 w-6 rounded-md bg-primary text-white flex items-center justify-center flex-shrink-0">
                        <Check size={11} />
                      </button>
                      <button onClick={() => setRenameId(null)}
                        className="h-6 w-6 rounded-md border border-slate-200 text-slate-400 flex items-center justify-center flex-shrink-0">
                        <X size={11} />
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold text-slate-900 leading-snug truncate pr-1">
                      {r.title}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-0.5">
                    <span className={`inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-slate-400">{gradeLabel}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{r.subject}</span>
                    {r.topic && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-slate-400 truncate max-w-[140px]">{r.topic}</span>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                    {viewedAgo ? `Viewed ${viewedAgo}` : `Edited ${editedAgo}`}
                    {r.isFavorite && (
                      <span className="text-amber-400 text-[10px]">★</span>
                    )}
                    {r.isArchived && (
                      <span className="text-slate-400 text-[10px] font-medium">Archived</span>
                    )}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => onOpenResource(r)}
                    className="h-8 px-3 rounded-lg bg-primary hover:bg-primary/90 text-white text-xs font-semibold transition-colors">
                    Open
                  </button>
                  <button onClick={() => toggleFavorite(r.id)}
                    title={r.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-all
                      ${r.isFavorite
                        ? 'border-amber-200 bg-amber-50 text-amber-500'
                        : 'border-slate-200 text-slate-300 hover:text-amber-400 hover:border-amber-200 hover:bg-amber-50'}`}>
                    <Star size={13} fill={r.isFavorite ? 'currentColor' : 'none'} />
                  </button>
                  <button onClick={() => startRename(r)} title="Rename"
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-300 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-all">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => duplicateResource(r.id)} title="Duplicate"
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-300 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-all">
                    <Copy size={13} />
                  </button>
                  <button
                    onClick={() => updateResource(r.id, { isArchived: !r.isArchived })}
                    title={r.isArchived ? 'Restore to library' : 'Archive'}
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-300 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-all">
                    {r.isArchived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                  </button>
                  <button onClick={() => setDeleteId(r.id)} title="Delete"
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-300 hover:text-red-500 hover:border-red-200 hover:bg-red-50 flex items-center justify-center transition-all">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function ts(iso?: string | null): number {
  return iso ? new Date(iso).getTime() : 0;
}

function relativeDate(iso: string): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 2)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
